// 📦 Import de React et des hooks nécessaires
import React, { useEffect, useState, useCallback  } from "react";
import axios from "axios";
import { Paper, Box, Typography, useMediaQuery } from "@mui/material";
import { Navigate } from "react-router-dom";

import { logDev } from "../utils/logger.js"; 

import SnackbarFeedback from "../pages/SnackbarFeedback.js";

import QuotaInfo from "../components/QuotaInfo.js";
import Sondage from "../components/Sondage.js";

import "./Survey.css";

// 🌐 Contexte pour récupérer l'ID du groupe courant
import { useGroupe } from "../context/GroupeContext.js";
// 🌐 Contexte de langue pour la traduction
import { useLanguage } from '../context/LanguageContext.js';

// 🔑 Hook pour savoir si l'utilisateur est admin dans ce groupe
import { useAdminRole } from "../hooks/useAdminRole.js";
//import { useSnackbar } from "../hooks/useSnackbar.js";


const Survey = ({ groupeId }) => {
  const { t } = useLanguage();      // Hook de traduction
  const { nomGroupeComplet, couleur, emoji } = useGroupe();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [reloadTrigger, setReloadTrigger] = useState(0);

  useEffect(() => {
    const header = document.querySelector('.header');
    const footer = document.querySelector('.footer');
    const nav = document.querySelector('.MuiBottomNavigation-root');

    const updateHeights = () => {
      if (header) setHeaderHeight(header.offsetHeight);
      if (footer) setFooterHeight(footer.offsetHeight);
      if (nav) setBottomNavHeight(nav.offsetHeight);
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, []);

  const topOffset = isMobile
    ? headerHeight + footerHeight
    : headerHeight; // ✅ sur desktop, le menu est déjà positionné sous le header

  const bottomOffset = isMobile ? bottomNavHeight : 0;

  logDev("[Survey] groupeId détecté", { groupeId });
  
  const [periodes, setPeriodes] = useState([]);
  const role = useAdminRole(groupeId);
  const [loading, setLoading] = useState(true);
  
  // 🔄 Fonction pour charger les périodes depuis l'API
  const fetchPeriodes = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/titresperiodes/${groupeId}`);
      
      // On garde uniquement les périodes valides avec date de début et de fin
      const mapped = (res.data || [])
        .filter(p => p?.start && p?.end)
        .map(p => ({ ...p, start: new Date(p.start), end: new Date(p.end) }));
        setPeriodes(mapped);
      } catch (err) {
      logDev("❌ Erreur chargement des périodes", { message: err.message, stack: err.stack });
    }
  }, [groupeId]);

  useEffect(() => {
    if (!groupeId) return;
      setLoading(true); // on relance le chargement à chaque changement de groupeId
      Promise.all([
        axios.get(`${process.env.REACT_APP_API}/groupes/${groupeId}`),
        axios.get(`${process.env.REACT_APP_API}/titresperiodes/${groupeId}`)
      ])
    .then(([resGroupe, resPeriodes]) => { 
      setPeriodes(
        (resPeriodes.data || [])
          .filter(p => p?.start && p?.end)
          .map(p => ({ ...p, start: new Date(p.start), end: new Date(p.end) }))
    );
  })
    .catch(err => logDev("❌ Erreur chargement groupe ou périodes", { message: err.message, stack: err.stack }))
    .finally(() => setLoading(false));
}, [groupeId]);

  const reloadPeriodesDepuisServeur = fetchPeriodes;

  // Bloquer le rendu tant que groupeId ou les données ne sont pas prêtes
  if (!groupeId) {
    return <Navigate to="/connexion" replace />;
  }

  if (role === null || loading) {
    return <div>Chargement…</div>;
  }

  // 🛡️ Déterminer si l'utilisateur est admin
  const isAdmin = role === "admin";

  logDev("[Survey] Hauteurs calculées", {
    headerHeight,
    bottomNavHeight,
    total: headerHeight + bottomNavHeight
  });

   // 🎨 Rendu du composant
  return (
    <div className="survey-container"
    style={{
    minHeight: "100vh",
    paddingTop: `${topOffset}px`,
    paddingBottom: `${bottomOffset}px`,
    boxSizing: "border-box",
    backgroundColor: "#f4ece2",
    overflowY: "auto",
    overflowX: "auto" 
  }}

    >
    {isAdmin && groupeId && (
      <QuotaInfo 
        groupeId={groupeId} 
        isAdmin={isAdmin}
        reloadTrigger={reloadTrigger}
      />
    )}
      <Paper
        elevation={1}
        sx={{
          backgroundColor: "#f4ece2",
          //width: "90vw",
          //maxWidth: isMobile ? "95vw" : "1200px",
          width: "100%",
          maxWidth: "1200px",
          padding: 2,
          margin: "0 auto",
          overflowX: "auto"   
        }}
      >

        {/* 🔔 Notice rôle : message différent pour admin ou utilisateur */}
            <div
              style={{
                backgroundColor: "#2f2f2fff",
                padding: "12px 16px",
                borderRadius: "6px",
                marginTop: isMobile ? 0 : "-8px",
                marginBottom: "12px",
                fontSize: "1rem",
                fontWeight: "bold",
                textAlign: "center",
                color: "#fff",
                border: "2px solid #d4af37", // doré
                boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                backgroundImage: "radial-gradient(circle at top left, rgba(255,255,255,0.05), transparent 70%)"
              }}
            >
              {isAdmin
                ? t('SurveyAdmin') 
                : t("SurveyUsers")}
            </div>

        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography 
          variant="h6" 
          align="center" 
          gutterBottom
          sx={{ color: couleur }}
          >
          <strong>{nomGroupeComplet}</strong>
          </Typography>
        </Box>

        <Sondage
          groupeId={groupeId}
          periodes={periodes}
          onReloadPériodes={reloadPeriodesDepuisServeur}
          role={role}
          isAdmin={isAdmin}
          snackbar={snackbar}
          setSnackbar={setSnackbar}
        />
        </Paper>
        
        <SnackbarFeedback
          snackbar={snackbar}
          setSnackbar={setSnackbar}
          bottomNavHeight={bottomNavHeight}
          emoji={emoji}
        />
    </div>
  );
};

export default Survey;
